import React, { useContext, useMemo, useRef, useState } from 'react';
import styles from './MfCreateNewComp.module.css';
import ModalInputField from 'components/shared/modal/ModalInputField';
import ModalDropdown from 'components/shared/modal/ModalDropdown';
import AuthenticationContext from 'config/context/authenticationContext';
import CompositionRecord from '../../composition-record/CompositionRecord';
import { ArrowRightIcon, PlusIcon } from 'features/compositions/assets/icons/icons';
import AddObject from '../../composition-record/AddObject';
import dataTypesConfig from 'features/compositions/constants/dataTypes.json';
import AddRecord from '../../composition-record/AddRecord';
import { useMfImport } from 'features/compositions/context/MfImportContext';
import { validateCompositionName, compositionNameProblem } from 'utils/validations';
import useClipboard from 'config/useClipboard';

/**
 * „3 lost, 4 lossy, 1 weakened" — da se težina vidi i dok je spisak sklopljen.
 * Broj sam ne govori ništa: osam napomena od kojih je svih osam `lossy` nije
 * isto što i osam u kojima su tri `lost`.
 */
/**
 * Stavke koje je korisnik u međuvremenu REŠIO — precrtavaju se, ne brišu.
 *
 * Izveštaj se pravi JEDNOM, na serveru, u koraku strukture, i opisuje IZVOR.
 * Kad korisnik prihvati ponudu i promeni tip u `decimal`, stavka je do 01.09.
 * i dalje pisala „stored as double" i savetovala ono što je upravo urađeno.
 *
 * Zna se po polju `from` — tipu koji stavka opisuje. Ako kolona danas nosi
 * drugi tip, savet više ne važi. Nov poziv serveru nije potreban: klijent već
 * drži i izmenjen tip i `scale`.
 *
 * NE BRIŠU SE. Stavka koja nestane izgleda kao da je nikad nije ni bilo, pa
 * korisnik ne vidi ŠTA je rešio — a i broj u zaglavlju bi se menjao dok kuca.
 */
export const withResolved = (degradations, tables, table, activeTable) => {
    // Ključ mora da prati ono što je server napisao. Kod VIŠE celina
    // `importsRouter` piše `${cilj}.${kolona}`; kod jedne piše golu kolonu.
    // `tables.length` razlikuje ta dva ista kao i server.
    //
    // Aktivna tabela se čita iz `table`, ne iz spiska: spisak se osvežava tek
    // pri promeni jezička (`selectTable`), pa bi izmena koju korisnik upravo
    // kuca bila nevidljiva dok ne pređe na drugu tabelu i vrati se.
    const svi = tables.length
        ? tables.map((t, i) => (i === activeTable ? { ...t, ...table } : t))
        : [{ target: null, ...table }];

    const poljePo = new Map();
    for (const t of svi) {
        for (const f of t.fields || []) {
            poljePo.set(t.target ? `${t.target}.${f.name}` : f.name, f);
        }
    }

    return degradations.map((d) => {
        // Bez cilja (napomena o uzorkovanju), bez `from` (starije rute), ili
        // cilj koji se ne nalazi među poljima — nema se šta uporediti, pa
        // stavka OSTAJE. Prećutati je zato što je ne umemo proveriti bilo bi
        // gore od toga da zastari.
        if (!d.target || !d.from) return { ...d, resolved: false };
        const polje = poljePo.get(d.target);
        if (!polje) return { ...d, resolved: false };
        // Ponuda oko spiska ima DVA izlaza: promeniti tip, ili ukinuti spisak.
        // Čarobnjak `[]` drži u `is_array`, pa se to mora gledati posebno.
        const ukinutSpisak = d.fromList === true && polje.is_array === false;
        return { ...d, resolved: polje.type !== d.from || ukinutSpisak };
    });
};

const severitySummary = (degradations) => {
    const counts = degradations.reduce((acc, d) => {
        const k = d.severity || 'note';
        acc[k] = (acc[k] || 0) + 1;
        return acc;
    }, {});
    return ['lost', 'lossy', 'weakened', 'note']
        .filter((k) => counts[k])
        .map((k) => `${counts[k]} ${k}`)
        .join(', ');
};

/**
 * Izveštaj kao čist tekst, za prenos dalje.
 *
 * Označavanje mišem ne valja: zaglavlje je dugme pa se ne da označiti, spisak
 * ima svoj klizač, a značka i cilj se pri kopiranju slepe u „notedec_money".
 * Ovde je razmak izričit i svaka stavka staje u dva reda.
 */
const degradationsAsText = (degradations) => {
    const otvorene = degradations.filter((d) => !d.resolved);
    const zaglavlje = `What this import cannot carry over (${otvorene.length}) — ${severitySummary(otvorene)}`;
    // Rešene idu i u kopirani tekst, označene — primalac inače ne vidi da je
    // nešto bilo prijavljeno pa sređeno.
    const stavke = degradations.map((d) =>
        `${d.resolved ? '[done] ' : ''}[${d.severity}] ${d.target ? d.target + ' — ' : ''}${d.what}\n    ${d.detail}`);
    return [zaglavlje, ...stavke].join('\n');
};

const MfCreateNewComp = () => {
    const { permissionsList } = useContext(AuthenticationContext);
    const { table, setTable, setStep, setStructs, selectedComposition, setSelectedComposition, selectedPermission, setSelectedPermission, allCompositionsList, fileType, degradations, tables, activeTable, selectTable } = useMfImport();

    // Izveštaj opisuje IZVOR i pravi se jednom; tipovi se od tada menjaju pod
    // njim. Obeležava se pri svakom iscrtavanju, jer se menja `table.fields`.
    const oznaceneDegradacije = useMemo(
        () => withResolved(degradations, tables, table, activeTable),
        [degradations, tables, table, activeTable],
    );
    const otvoreneDegradacije = oznaceneDegradacije.filter((d) => !d.resolved);

    // Jedan prolaz sa više celina (tabela, kolekcija, obrazaca ključeva). Tada
    // imena kompozicija dolaze IZ imena celina, pa se ne unose — a pregled šeme
    // se prebacuje jezičcima, jer korisnik svaku treba da vidi pre deploy-a.
    const many = (tables?.length ?? 0) > 1;

    const [view, setView] = useState('records');
    const { writeClipboard } = useClipboard();
    const [copied, setCopied] = useState(false);
    const [nra, setNra] = useState(1); // 3=Add field,4=Add index,5=Add array
    const [recordTypes, setRecordTypes] = useState(dataTypesConfig.dataTypes);
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);
    // Kod više celina imena nisu u polju za unos, pa nemaju gde da prikažu
    // grešku validacije. Bez ovoga klik na „Next" ne uradi ništa i ne kaže
    // zašto — a razlog je po pravilu ime koje već postoji u bazi.
    const [nameIssue, setNameIssue] = useState(null);
    // Sidro za fokusiranje polja sa imenom; vidi `goToAuth`.
    const nameFieldRef = useRef(null);
    const [lossOpen, setLossOpen] = useState(false);


    const normalizeAIFlag = (fields) => {
        const firstIndexPosition = fields.findIndex(f => f.is_index);

        return fields.map((f, i) => {
            if (!f.is_index) {
                return { ...f, ai: false };
            }

            if (i === firstIndexPosition) {
                return { ...f };
            }

            return { ...f, ai: false };
        });
    };

    const normalizeIndexFlag = (fields) => {
        return fields.map(f => {
            if (f.is_index) {
                return { ...f, is_vector: false }; // Ako je index true, vector je false
            }
            return f;
        });
    };

    const normalizeFields = (fields) => {
        const aiNormalized = normalizeAIFlag(fields);
        const indexNormalized = normalizeIndexFlag(aiNormalized);
        return indexNormalized;
    };

    const handleDragStart = (index) => setDraggedItemIndex(index);

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedItemIndex === null || draggedItemIndex === index) return;

        setTable(prev => {
            const newFields = [...prev.fields];
            const [removed] = newFields.splice(draggedItemIndex, 1);
            newFields.splice(index, 0, removed);

            const normalized = normalizeFields(newFields);
            setDraggedItemIndex(index);

            return { ...prev, fields: normalized };
        });
    };

    const handleDragEnd = () => setDraggedItemIndex(null);

    const handleItemChange = (index, key, value) => {
        setTable(prev => {
            const updated = [...prev.fields];
            updated[index] = { ...updated[index], [key]: value };
            const normalized = normalizeFields(updated);
            return { ...prev, fields: normalized };
        });
    };

    const handleDeleteItem = (index) => {
        setTable(prev => {
            const updated = [...prev.fields];
            if (!updated[index]?.new) return prev;
            updated.splice(index, 1);
            return { ...prev, fields: updated };
        });
    };

    const goToAuth = () => {
        if (many) {
            // Sva imena, ne samo tekuće — inače bi uvoz stao na trećoj tabeli,
            // posle deploy-a prve dve.
            const bad = tables.find((t) => !validateCompositionName(t.name, allCompositionsList));
            if (bad) {
                setNameIssue(
                    allCompositionsList.has(bad.name)
                        ? `Composition '${bad.name}' already exists in this database. `
                          + 'An import can add compositions, but cannot change an existing one — '
                          + 'clear that one from the selection.'
                        : `'${bad.name}' is not a usable composition name`
                );
                return;
            }
            setNameIssue(null);
            setStep(3);
            return;
        }
        // Jedna kompozicija. Ranije je ovde stajalo `if (isValid) setStep(3)` i
        // ništa više: kad ime nije važeće, klik na „Go to authorization" prosto
        // se ne bi završio ničim — bez poruke, bez greške u konzoli, bez ikakve
        // naznake da je uzrok ime. Najčešći slučaj je bio prazno polje, jer
        // prelazak na ovaj jezičak ime briše.
        const problem = compositionNameProblem(selectedComposition, allCompositionsList);
        if (problem) {
            setNameIssue(problem);
            // Poruka sama ne kaže GDE se radnja obavlja, a polje je iznad spiska
            // polja i lako promakne. Fokus vodi na njega. `ModalInputField` je
            // zajednička komponenta i ne prosleđuje ref, pa se do input-a dolazi
            // kroz omotač — jeftinije nego menjati komponentu koju koristi još
            // desetak mesta.
            nameFieldRef.current?.querySelector('input')?.focus();
            return;
        }
        setNameIssue(null);
        setStep(3);
    };

    const isCsv = fileType() === 'csv';

    return (
        <div className={styles.mf_create_new_comp}>
            {view === 'records' && (
                <div className={styles.mf_create_new_comp_container}>
                    {/* Jezičci po celini. Prikaz je uvek jedna šema, jer je i
                        pregled po jednoj — ali prelazak čuva ono što je na
                        prethodnoj izmenjeno (vidi `selectTable`). */}
                    {many && (
                        <div className={styles.table_tabs}>
                            {tables.map((t, i) => (
                                <button
                                    key={t.target || t.name}
                                    type="button"
                                    className={i === activeTable ? styles.table_tab_active : styles.table_tab}
                                    onClick={() => selectTable(i)}
                                    title={t.target}
                                >
                                    {t.name}
                                    <em>{(t.fields || []).length}</em>
                                </button>
                            ))}
                        </div>
                    )}
                    {nameIssue && <p className={styles.name_issue}>{nameIssue}</p>}
                    <div className={styles.header}>
                        {many ? (
                            // Ime je izvedeno iz imena celine u izvoru, pa se ne
                            // unosi. Pokazuje se i odakle je, jer se za Redis
                            // razlikuje: `patient:*` daje kompoziciju `patient`.
                            <p className={styles.derived_name}>
                                {table?.name}
                                {table?.target && table.target !== table?.name && (
                                    <span> ← {table.target}</span>
                                )}
                            </p>
                        ) : (
                            <div ref={nameFieldRef} className={styles.name_field_anchor}>
                            <ModalInputField
                                type="text"
                                placeholder="Composition name"
                                value={selectedComposition}
                                // Poruka se sklanja čim korisnik krene da menja ime —
                                // inače stoji uz polje koje je u međuvremenu ispravljeno.
                                setValue={(val) => {
                                    setSelectedComposition(val);
                                    if (nameIssue) setNameIssue(null);
                                }}
                                validator={(val) => validateCompositionName(val, allCompositionsList)}
                            />
                            </div>
                        )}
                        <ModalDropdown
                            title={selectedPermission || "Composition permission"}
                            options={permissionsList}
                            setOption={setSelectedPermission}
                        />
                    </div>
                    <div className={styles.important_notes}>
                        <span>Note: Use Uint 64bit as the first index with AI enabled.</span>
                    </div>
                    <div className={styles.records_area}>
                        <div className={styles.records_container}>
                            <div className={styles.records_header}>
                                <span></span>
                                <span>Index</span>
                                <span>AI</span>
                                <span>Array</span>
                                <span>Name</span>
                                <span>Type</span>
                                <span>Default vaue</span>
                            </div>

                            <div className={styles.records}>
                                {table?.fields.map((item, idx) => (
                                    <CompositionRecord
                                        key={item.name}
                                        item={item}
                                        index={idx}
                                        isDragged={idx === draggedItemIndex}
                                        onDragStart={handleDragStart}
                                        onDragOver={handleDragOver}
                                        onDragEnd={handleDragEnd}
                                        onDrop={handleDragEnd}
                                        onChange={handleItemChange}
                                        onDelete={handleDeleteItem}
                                        dataTypes={recordTypes}
                                    />
                                ))}
                            </div>

                            {/*
                                Šta se pri prelasku gubi ili slabi — UNIQUE koji
                                prestaje da važi, DECIMAL koji gubi cifre, strani
                                ključ koji postaje obična kolona, NULL koji dobija
                                zamensku vrednost.

                                Stoji ISPOD spiska polja i neposredno iznad dugmeta
                                koje vodi na deploy. Ranije je bio iznad spiska, pa
                                je sa osam stavki i punim opisima potiskivao i spisak
                                i dugme ispod ekrana — korisnik nije video kuda dalje.

                                Sklopljen je podrazumevano, ali zaglavlje nosi broj po
                                težini, pa se ono najvažnije vidi bez klika.

                                Od 27.08.2026. ovde stižu i napomene izvora bez šeme
                                (CSV, JSON, XML, Redis), za koje je odeljak ranije bio
                                prazan i uopšte se nije crtao. Njima se struktura sada
                                MERI nad celim izvorom, pa o opsegu nema šta da se
                                javi — tip je nalaz, ne pogodak. Ostaje ono što ni
                                merenje ne može da zna: da li je `double` kolona novac,
                                pa se `decimal` nudi uz izmerene `precision` i `scale`.
                            */}
                            {oznaceneDegradacije.length > 0 && (
                                <div className={styles.degradations}>
                                    <div className={styles.degradations_head}>
                                        <button
                                            type="button"
                                            className={styles.degradations_title}
                                            onClick={() => setLossOpen((v) => !v)}
                                        >
                                            {/* `user-select` na samom natpisu: tekst UNUTAR dugmeta
                                                pregledač podrazumevano ne dâ da se označi, pa se
                                                zaglavlje izveštaja nije moglo kopirati. */}
                                            <span className={styles.degradations_label}>
                                                What this import cannot carry over ({otvoreneDegradacije.length})
                                            </span>
                                            <em>{severitySummary(otvoreneDegradacije)}</em>
                                            <i>{lossOpen ? '▾' : '▸'}</i>
                                        </button>
                                        {/* Izveštaj se prenosi drugima — kolegi, u tiket, u poruku.
                                            Označavanje mišem preko klizačevog spiska je mučno, a
                                            značka i cilj se pri tom slepe u „notedec_money". */}
                                        <button
                                            type="button"
                                            className={styles.degradations_copy}
                                            title="Copy the report as text"
                                            onClick={() => {
                                                writeClipboard(degradationsAsText(oznaceneDegradacije));
                                                setCopied(true);
                                                setTimeout(() => setCopied(false), 1500);
                                            }}
                                        >
                                            {copied ? 'Copied' : 'Copy'}
                                        </button>
                                    </div>
                                    {lossOpen && (
                                        <ul>
                                            {oznaceneDegradacije.map((d, i) => (
                                                <li
                                                    key={i}
                                                    className={`${styles[`sev_${d.severity}`] || ''} ${d.resolved ? styles.resolved : ''}`}
                                                >
                                                    <b>{d.severity}</b>
                                                    {' '}
                                                    {d.target && <span className={styles.deg_target}>{d.target}</span>}
                                                    {' — '}{d.what}
                                                    <div className={styles.degradation_detail}>{d.detail}</div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                            <div className={styles.records_actions}>
                                {nra === 1 && (
                                    <div className={styles.records_actions__1}>
                                        <button onClick={() => setNra(2)}>Add new record <PlusIcon /></button>
                                        <button onClick={goToAuth}>Go to authorization <ArrowRightIcon /></button>
                                    </div>
                                )}
                                {nra === 2 && (
                                    <div className={styles.records_actions__2}>
                                        <button onClick={() => setNra(3)}>Add field</button>
                                        <button onClick={() => setNra(4)}>Add index</button>
                                        <button onClick={() => setNra(5)}>Add array</button>
                                        {!isCsv && <button onClick={() => setView('add-object')}>Add object</button>}
                                    </div>
                                )}
                                {nra === 3 && <AddRecord table={table} setTable={setTable} setNra={setNra} isIndex={false} isArray={false} dataTypes={recordTypes} />}
                                {nra === 4 && <AddRecord table={table} setTable={setTable} setNra={setNra} isIndex={true} isArray={false} dataTypes={recordTypes} />}
                                {nra === 5 && <AddRecord table={table} setTable={setTable} setNra={setNra} isIndex={false} isArray={true} dataTypes={recordTypes} />}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {view === 'add-object' && (
                <AddObject
                    dataTypes={recordTypes}
                    setDataTypes={setRecordTypes}
                    setView={setView}
                    setStructs={setStructs}
                />
            )}
        </div>
    );
};

export default MfCreateNewComp;
